// The dock: dockview rendering the three bars around the board (slice 05).
//
// Groups have STABLE IDS — `left`, `right`, `bottom`, `center` — so the
// serialised layout says which bar holds what (`barsOfDock` in vtt/shared
// reads the same ids). A side bar's group holds its tools as TABS
// (qa-runs#404), one showing; the bottom bar and the second window's centre
// hold one tool; an empty bar holds the `empty:<slot>` placeholder that
// carries its `+`. Drag and drop stays off and every group is locked: a tool
// moves through the menus, the `+` and each tab's menu, never by dragging. A
// bar out of room folds its extra tabs into dockview's overflow menu (the ▸).
// The main window's centre is the board, header hidden, never a tool.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DockviewReact, themeDark, themeLight,
  type DockviewApi, type DockviewReadyEvent, type IDockviewHeaderActionsProps, type IDockviewPanelHeaderProps, type IDockviewPanelProps,
} from 'dockview-react';
import { SLOTS, isTabbedSlot, tabIcon, type DockBars, type DockLayout, type SlotId, type ViewId } from '@dragons-table/vtt-shared';
import { useShell } from './context';
import { BoardPlace, KeptBoard, PanelMemory, useKeptBoard } from './keptBoard';
import {
  BOARD_PANEL, barsOfDockSafe, emptyIdFor, otherSide, panelIdFor, showingOf, stockSlots, toolOfPanel, type DockHandle, type DockSlots, type ToolId,
} from './dockHandle';
import { SLOT_LABELS, ToolBody, toolById } from './tools';
import { bottomBarHeight, stripHeight } from './stripHeight';
import { STOCK_BOTTOM_HEIGHT, barPlan, phoneBarWidth, rootTextScale, stockSideWidth } from './barFit';
import { coarsePointer } from './device';
import type { DeviceClass } from '@dragons-table/vtt-shared';

export { STOCK_BOTTOM_HEIGHT, STOCK_SIDE_WIDTH, rootTextScale, stockSideWidth } from './barFit';

const isSlot = (id: string): id is SlotId => (SLOTS as readonly string[]).includes(id);
const titleOf = (tool: ToolId) => toolById(tool)?.title ?? tool;

// ── Parts dockview renders ─────────────────────────────────────────────────

function ToolPanel(props: IDockviewPanelProps<{ tool: string }>) {
  // A named region the keyboard can reach and scroll (issue 1175): Tab lands on it, the arrow keys scroll it.
  return (
    <div role="region" aria-label={titleOf(props.params.tool)} tabIndex={0} className="vtt-tool h-full overflow-auto bg-stone-900" data-testid={`panel-${props.params.tool}`}>
      <ToolBody tool={props.params.tool} />
    </div>
  );
}

/**
 * An empty bar (issue 1179; the finding on #1064): a slim strip holding its + alone, on every screen, so a bar with
 * nothing on it never takes the board's room. "Nothing here yet" is said, not shown: the +'s name and its hint. The
 * bar's header is hidden while it is empty (`fitBars`), so this + is the bar's only one.
 */
export function EmptyPanel(props: IDockviewPanelProps<{ slot: SlotId }>) {
  const { openAdd } = useShell();
  const slot = props.params.slot;
  const name = `Add to the ${SLOT_LABELS[slot].toLowerCase()}`;
  return (
    <div className="flex h-full items-center justify-center bg-stone-900" data-testid={`empty-${slot}`}>
      <span className="sr-only">Nothing here yet.</span>
      <button type="button" className="vtt-bar-plus flex h-full max-h-11 w-full max-w-11 items-center justify-center rounded text-lg text-stone-300 hover:bg-stone-800"
        aria-label={name} title={`Nothing here yet. ${name}`} data-testid={`add-${slot}`} onClick={() => openAdd(slot)}>
        +
      </button>
    </div>
  );
}

function BoardPanel() {
  const { board, phone } = useShell();
  // A narrow screen's board keeps its room (issue 1179): the frame around it is thinner below 640 px.
  // Kept across a layout switch (qa-runs 1199): the panel lends the board a place; the board itself is never remounted.
  return <div className={`h-full overflow-auto bg-stone-950 p-1 ${phone ? '' : 'sm:p-3'}`}><BoardPlace board={board} /></div>;
}

/**
 * A bar's tab: the tool's title, or the icon the person picked for it (qa-runs#404), the title then its name for a
 * screen reader and its hover. No control inside it — the tab is itself interactive, and a button in it is a nested
 * interactive (axe). The tab itself answers the keyboard: Enter or Space shows it; the context-menu key, Shift+F10 or a
 * right-click (a long press on a tablet) opens its menu.
 */
function Tab(props: IDockviewPanelHeaderProps<{ tool?: string; slot?: SlotId }>) {
  const { icons, openTabMenu } = useShell();
  const tool = props.params.tool;
  const title = props.api.title ?? (tool ? titleOf(tool) : 'Nothing here yet');
  const icon = tool ? tabIcon(icons?.[tool]) : null;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current?.closest<HTMLElement>('.dv-tab');
    if (!el || !tool) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.api.setActive(); }
      else if ((e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) && openTabMenu) { e.preventDefault(); openTabMenu(tool); }
    };
    const onMenu = (e: MouseEvent) => { if (openTabMenu) { e.preventDefault(); openTabMenu(tool); } };
    el.addEventListener('keydown', onKey);
    el.addEventListener('contextmenu', onMenu);
    el.setAttribute('aria-label', title);
    return () => { el.removeEventListener('keydown', onKey); el.removeEventListener('contextmenu', onMenu); };
  }, [tool, title, openTabMenu, props.api]);
  // The bar's title in the frame's face (issue 1541): gold Cinzel, small capitals by uppercase and spacing.
  return (
    <div ref={ref} className="vtt-tab-title px-3 font-display text-[0.8125rem] font-semibold uppercase tracking-[0.1em] text-vtt-gold" data-testid={tool ? `tab-${tool}` : undefined} title={icon ? title : undefined}>
      {icon ? <><span aria-hidden="true" className="text-base normal-case tracking-normal" data-testid={`tab-icon-${tool}`}>{icon.glyph}</span><span className="sr-only">{title}</span></> : title}
    </div>
  );
}

/** What every bar carries in its header: the way off the bar for its tool, and the `+` (design/07 §1). Not on the main window's board. */
function BarActions(props: IDockviewHeaderActionsProps) {
  const { openAdd, demote, openStatBlockSettings, openTabMenu } = useShell();
  const id = props.group.id;
  if (!isSlot(id)) return null;
  const tool = toolOfPanel(props.activePanel?.id);
  // An empty bar's header is hidden and its strip IS its + (issue 1179): nothing here, so the + is never there twice.
  if (!tool) return null;
  // Quiet beside the gold title (issue 1541): muted, not the mock-up's faint, which measures under 3:1 on the bar.
  return (
    <div className="flex items-center">
      {/* The stat block's settings (issue 1575), on the Properties bar, for whoever has them (a DM). */}
      {tool === 'properties' && openStatBlockSettings && (
        <button type="button" className="rounded px-1 text-sm text-vtt-muted hover:bg-stone-800 hover:text-vtt-fg" aria-label="Stat block settings"
          title="Stat block sections: how a creature's stat block is grouped (Layout menu)" data-testid="stat-block-gear" onClick={openStatBlockSettings}>
          ⚙
        </button>
      )}
      {/* The showing tab's menu (qa-runs#404): move it left, right or to the other bar, its icon, close it. */}
      {openTabMenu && (
        <button type="button" className="rounded px-1 text-sm text-vtt-muted hover:bg-stone-800 hover:text-vtt-fg"
          aria-label={`${titleOf(tool)} tab menu`} title="Move this tab, or give it an icon" data-testid={`tab-menu-${tool}`} onClick={() => openTabMenu(tool)}>
          ⋮
        </button>
      )}
      <button type="button" className="rounded px-1 text-sm text-vtt-muted hover:bg-stone-800 hover:text-vtt-fg"
        aria-label={`Remove ${titleOf(tool)} from the bar`} onClick={() => demote(tool)}>
        ×
      </button>
      <button type="button" className="mx-1 rounded px-2 text-sm text-vtt-muted hover:bg-stone-800 hover:text-vtt-fg"
        aria-label={`Add to the ${SLOT_LABELS[id].toLowerCase()}`} data-testid={`add-${id}`} onClick={() => openAdd(id)}>
        +
      </button>
    </div>
  );
}

const components = { tool: ToolPanel, empty: EmptyPanel, board: BoardPanel };
const tabComponents = { tab: Tab };

// ── The handle ─────────────────────────────────────────────────────────────

function addTo(api: DockviewApi, slot: SlotId, tool: ToolId | null, index?: number): void {
  if (tool) {
    api.addPanel({ id: panelIdFor(tool), component: 'tool', tabComponent: 'tab', title: titleOf(tool), params: { tool }, position: { referenceGroup: slot, ...(index === undefined ? {} : { index }) } });
  } else {
    api.addPanel({ id: emptyIdFor(slot), component: 'empty', tabComponent: 'tab', title: 'Nothing here yet', params: { slot }, position: { referenceGroup: slot } });
  }
}

/** The stock, or a shipped preset's bars (the Fight layout, qa-runs#404): built the same way, extra tabs after the first. */
function buildStock(api: DockviewApi, view: ViewId, stock: DockSlots, device: DeviceClass, extra?: DockBars): void {
  api.clear();
  const isMain = view === 'main';
  api.addGroup({ id: 'center', direction: 'right', locked: true, hideHeader: isMain });
  if (isMain) api.addPanel({ id: BOARD_PANEL, component: 'board', title: 'The table', position: { referenceGroup: 'center' } });
  else addTo(api, 'center', stock.center);
  // The bottom bar first, so it spans the whole width; the sides then split the row above it.
  api.addGroup({ id: 'bottom', referenceGroup: 'center', direction: 'below', locked: true });
  addTo(api, 'bottom', stock.bottom);
  api.addGroup({ id: 'left', referenceGroup: 'center', direction: 'left', locked: true });
  addTo(api, 'left', stock.left);
  api.addGroup({ id: 'right', referenceGroup: 'center', direction: 'right', locked: true });
  addTo(api, 'right', stock.right);
  // Sizes are set after the grid exists (an initial size on addGroup is not honoured for a split of an existing cell).
  // The stock sizes: the bottom bar's start (the strip refits it), and the sides at the width this screen gives them.
  const plan = planFor(api, device);
  api.getGroup('bottom')?.api.setSize({ height: plan.bottom.empty ? plan.bottom.px : STOCK_BOTTOM_HEIGHT });
  api.getGroup('left')?.api.setSize({ width: plan.left.px });
  api.getGroup('right')?.api.setSize({ width: plan.right.px });
  if (extra) {
    for (const slot of SLOTS) {
      if (!isTabbedSlot(slot)) continue;
      for (const tool of extra[slot].tabs.slice(1)) addTo(api, slot, tool);
      const first = stock[slot];
      if (first) api.getPanel(panelIdFor(first))?.api.setActive();
    }
  }
}

function barsIn(api: DockviewApi): DockBars {
  const out = {} as DockBars;
  for (const slot of SLOTS) {
    const group = api.getGroup(slot);
    const tabs = (group?.panels ?? []).map((p) => toolOfPanel(p.id)).filter((t): t is ToolId => t !== null);
    const active = toolOfPanel(group?.activePanel?.id);
    out[slot] = { tabs, showing: active && tabs.includes(active) ? active : tabs[0] ?? null };
  }
  return out;
}

function slotsIn(api: DockviewApi): DockSlots {
  return showingOf(barsIn(api));
}

function planFor(api: DockviewApi, device: DeviceClass) {
  return barPlan(slotsIn(api), { device, width: typeof window === 'undefined' ? 1280 : window.innerWidth, coarse: coarsePointer(), scale: rootTextScale() });
}

/** A bar this narrow or short has been an empty strip: given a tool, it grows back to the bar's width. */
const STRIP_MAX_PX = 60;

/**
 * Each bar sized for what it holds (issue 1179): an empty bar pinned to a strip with its + alone and its header hidden;
 * a bar holding a tool freed, and if it was a strip, grown back to the width the screen gives a side bar. A saved
 * arrangement keeps its own sizes otherwise, within the plan's floor and cap: never so wide the board loses its share
 * (issue 1467), never so narrow this text size wraps a word a line (issue 1468). Run again when the window or the text
 * size changes. Made as a load is, never saved as the person's change unless it rode on one.
 */
function fitBars(api: DockviewApi, view: ViewId, device: DeviceClass, phone = false): void {
  const plan = planFor(api, device);
  // The dock's own marks, for index.css: which bar each group is, and that this is the phone layout.
  for (const slot of ['left', 'right', 'bottom'] as const) (api.getGroup(slot) as unknown as { element?: HTMLElement } | undefined)?.element?.classList.add(`vtt-bar-${slot}`);
  if (phone) { fitPhoneBars(api); return; }
  for (const slot of ['left', 'right', 'bottom'] as const) {
    const group = api.getGroup(slot);
    if (!group) continue;
    const bar = plan[slot];
    const side = slot !== 'bottom';
    group.model.header.hidden = bar.empty;
    if (bar.empty) {
      group.api.setConstraints(side ? { minimumWidth: bar.px, maximumWidth: bar.px } : { minimumHeight: bar.px, maximumHeight: bar.px });
      group.api.setSize(side ? { width: bar.px } : { height: bar.px });
    } else {
      if (!side) {
        group.api.setConstraints({ maximumHeight: Number.MAX_SAFE_INTEGER });
        if (group.api.height <= STRIP_MAX_PX) group.api.setSize({ height: bar.px });
        continue;
      }
      // Held between the plan's floor and cap (issues 1467, 1468): a dragged or saved width stands while it leaves the
      // board its share and carries this text size's line; outside them it is brought in, as a load is, never saved.
      const min = bar.min ?? Math.min(100, bar.px);
      const max = bar.max ?? Number.MAX_SAFE_INTEGER;
      group.api.setConstraints({ minimumWidth: min, maximumWidth: max });
      const now = group.api.width;
      if (now <= STRIP_MAX_PX) group.api.setSize({ width: bar.px });
      else if (now > max) group.api.setSize({ width: max });
      else if (now < min) group.api.setSize({ width: min });
    }
  }
  if (view !== 'main') api.getGroup('center')?.api.setConstraints({ minimumWidth: 100 });
}

/**
 * The phone layout (issue 1242). The dock is laid out wider than the screen by one bar on each side, so the side bars
 * lie off the screen at a fixed width and the board has the whole of it; index.css slides the opened one over the
 * board, which never resizes for it. Nothing here is the person's change: `fitBars` undoes it off a phone.
 */
function fitPhoneBars(api: DockviewApi): void {
  const px = phoneBarWidth(typeof window === 'undefined' ? 320 : window.innerWidth);
  for (const slot of ['left', 'right'] as const) {
    const group = api.getGroup(slot);
    if (!group) continue;
    group.model.header.hidden = group.panels.every((p) => !toolOfPanel(p.id));
    group.api.setConstraints({ minimumWidth: px, maximumWidth: px });
    group.api.setSize({ width: px });
  }
  const bottom = api.getGroup('bottom');
  if (!bottom) return;
  // The strip says what it is: its tab's header is room the board needs.
  bottom.model.header.hidden = true;
  if (bottom.panels.every((p) => !toolOfPanel(p.id))) {
    bottom.api.setConstraints({ minimumHeight: 44, maximumHeight: 44 });
    bottom.api.setSize({ height: 44 });
  } else bottom.api.setConstraints({ maximumHeight: Number.MAX_SAFE_INTEGER });
}

/**
 * The bottom bar fitted to the initiative strip's content (issue 1070), when the strip is what it holds: the tab header
 * (the group's height less its panel's) plus the strip's natural height. A fit is not the person's change, so it is
 * made as a load is — not saved as a layout.
 */
function fitStrip(api: DockviewApi, content: number, loading: { current: boolean }): void {
  const group = api.getGroup('bottom');
  if (!group || toolOfPanel(group.activePanel?.id ?? group.panels[0]?.id) !== 'initiative') return;
  const panel = document.querySelector('[data-testid="panel-initiative"]');
  const header = panel ? group.api.height - panel.getBoundingClientRect().height : 0;
  const want = bottomBarHeight(header, content, rootTextScale());
  // dockview holds every group at 100 px or more (MINIMUM_DOCKVIEW_GROUP_PANEL_HEIGHT), which kept the idle strip's bar
  // at 100 however small the strip was (seen on vtt-dev): the bar's floor is lowered to what the strip asks.
  group.api.setConstraints({ minimumHeight: Math.min(want, 100) });
  if (Math.abs(group.api.height - want) < 1) return;
  const was = loading.current;
  loading.current = true;
  group.api.setSize({ height: want });
  setTimeout(() => { if (!was) loading.current = false; }, 0);
}


/**
 * A loaded layout is usable when every slot group is there holding a panel — one, or a side bar's tabs, never a
 * placeholder beside a tool — and the main window has its board.
 */
function intact(api: DockviewApi, view: ViewId): boolean {
  for (const slot of SLOTS) {
    const group = api.getGroup(slot);
    if (!group || group.panels.length === 0) return false;
    if (group.panels.length > 1 && (!isTabbedSlot(slot) || group.panels.some((p) => !toolOfPanel(p.id)))) return false;
  }
  if (view === 'main' && !api.getPanel(BOARD_PANEL)) return false;
  return true;
}

export function makeHandle(api: DockviewApi, view: ViewId, loading: { current: boolean }, stock: DockSlots = stockSlots(view), device: DeviceClass = 'desktop', isPhone: () => boolean = () => false): DockHandle {
  const fit = () => fitBars(api, view, device, isPhone());
  const bars = (): DockBars => barsIn(api);
  /** The bar's whole content replaced by one tool, or by its placeholder. */
  const swap = (slot: SlotId, tool: ToolId | null) => {
    const group = api.getGroup(slot);
    if (!group) return;
    const old = [...group.panels];
    addTo(api, slot, tool);
    for (const p of old) api.removePanel(p);
  };
  /** A tool's tab gone from its bar; the last leaves the placeholder. */
  const drop = (tool: ToolId) => {
    const panel = api.getPanel(panelIdFor(tool));
    if (!panel) return;
    const slot = panel.group.id as SlotId;
    if (panel.group.panels.length <= 1) swap(slot, null);
    else api.removePanel(panel);
  };
  const put = (slot: SlotId, tool: ToolId, index?: number) => {
    const group = api.getGroup(slot);
    if (!group) return;
    if (isTabbedSlot(slot) && group.panels.some((p) => toolOfPanel(p.id))) addTo(api, slot, tool, index);
    else swap(slot, tool);
  };
  return {
    place(slot, tool) {
      if (view === 'main' && slot === 'center') return;
      const now = bars();
      if (now[slot].tabs.includes(tool)) { api.getPanel(panelIdFor(tool))?.api.setActive(); return; }
      if (SLOTS.some((s) => s !== slot && now[s].tabs.includes(tool))) drop(tool);
      put(slot, tool);
      fit();
    },
    close(tool) {
      drop(tool);
      fit();
    },
    clear(slot) {
      if (view === 'main' && slot === 'center') return;
      if (bars()[slot].tabs.length > 0) swap(slot, null);
      fit();
    },
    show(tool) {
      api.getPanel(panelIdFor(tool))?.api.setActive();
    },
    move(tool, where) {
      const panel = api.getPanel(panelIdFor(tool));
      if (!panel) return;
      const from = panel.group.id as SlotId;
      if (!isTabbedSlot(from)) return;
      if (where === 'other') {
        const to = otherSide(from)!;
        drop(tool);
        put(to, tool);
        fit();
        return;
      }
      const tabs = bars()[from].tabs;
      const i = tabs.indexOf(tool);
      const j = where === 'left' ? i - 1 : i + 1;
      if (i < 0 || j < 0 || j >= tabs.length) return;
      // Out and back in at its new place, shown: dockview's own move is a drag's, and drag and drop is off.
      api.removePanel(panel);
      addTo(api, from, tool, j);
      api.getPanel(panelIdFor(tool))?.api.setActive();
    },
    slots: () => slotsIn(api),
    bars,
    toJSON: () => api.toJSON() as unknown as DockLayout,
    load(layout) {
      loading.current = true;
      let ok = false;
      const shipped = barsOfDockSafe(layout);
      if (shipped) {
        buildStock(api, view, showingFirst(shipped), device, shipped);
        ok = true;
      } else if (layout) {
        try {
          api.fromJSON(layout as never);
          ok = intact(api, view);
        } catch (err) {
          console.warn('layout could not be loaded; the stock arrangement is used', err instanceof Error ? err.message : err);
        }
      }
      if (!ok) buildStock(api, view, stock, device);
      fit();
      // dockview reports a layout change on a microtask after the change, so
      // the load stays "loading" until the timers run: a loaded layout is
      // never re-saved as if the person had made it (that re-save once
      // ping-ponged between two windows of the same person).
      setTimeout(() => { loading.current = false; }, 0);
    },
  };
}

/** Each bar's first tab, for building a shipped preset as the stock is built. */
function showingFirst(bars: DockBars): DockSlots {
  const out = {} as DockSlots;
  for (const s of SLOTS) out[s] = bars[s].tabs[0] ?? null;
  return out;
}

// ── The component ──────────────────────────────────────────────────────────

export interface DockProps {
  view: ViewId;
  /** What this window showed last, or null for the stock arrangement. */
  initial: DockLayout | null;
  onReady: (handle: DockHandle) => void;
  /** Every change the person makes: a placement, a removal, a resize. Not fired while a layout is being loaded. */
  onChange: (layout: DockLayout) => void;
  /** The side bar opened over the board in the phone layout (issue 1242), or none. */
  openBar?: 'left' | 'right' | null;
}

export function Dock({ view, initial, onReady, onChange, openBar = null }: DockProps) {
  // The site's theme (issue 1041): dockview's chrome light or dark with the rest of the shell.
  const { settings, stock: given, device = 'desktop', board, phone = false } = useShell();
  // The main window's board, rendered once and lent to whichever board panel the layout has now (qa-runs 1199).
  const kept = useKeptBoard(board, view === 'main');
  const memory = useRef(new Map<string, unknown>());
  const stock = given ?? stockSlots(view);
  const dockOf = useRef({ stock, device, phone });
  dockOf.current = { stock, device, phone };
  const loading = useRef(false);
  const latest = useRef({ initial, onReady, onChange });
  latest.current = { initial, onReady, onChange };
  const unfit = useRef<(() => void) | null>(null);
  const refitRef = useRef<(() => void) | null>(null);
  useEffect(() => () => { unfit.current?.(); }, []);

  const ready = useCallback((event: DockviewReadyEvent) => {
    const handle = makeHandle(event.api, view, loading, dockOf.current.stock, dockOf.current.device, () => dockOf.current.phone);
    handle.load(latest.current.initial);
    event.api.onDidLayoutChange(() => {
      if (!loading.current) latest.current.onChange(handle.toJSON());
    });
    latest.current.onReady(handle);
    unfit.current?.();
    const unsubscribe = stripHeight.subscribe((px) => fitStrip(event.api, px, loading));
    // The bar's header grows after the strip has reported when the text size changes or the reading typeface finishes
    // loading (issue 1174: at 200% the strip was cut off below the screen on vtt-dev), and the strip's own height does
    // not change, so nothing refitted the bar. Its panel's size is watched, and any change refits it to the last height.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
      const px = stripHeight.current();
      if (px !== null) fitStrip(event.api, px, loading);
    });
    const watch = () => { const panel = document.querySelector('[data-testid="panel-initiative"]'); if (panel && observer) observer.observe(panel); };
    watch();
    const stopWatching = event.api.onDidLayoutChange(() => watch());
    // A window narrowed or widened, or the text size changed (issues 1467, 1468): the bars are refitted then and there,
    // not on the next reload. After dockview has laid itself out for the new size; as a load, so never saved.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refit = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const was = loading.current;
        loading.current = true;
        fitBars(event.api, view, dockOf.current.device, dockOf.current.phone);
        setTimeout(() => { if (!was) loading.current = false; }, 0);
      }, 60);
    };
    refitRef.current = refit;
    window.addEventListener('resize', refit);
    unfit.current = () => { unsubscribe(); observer?.disconnect(); stopWatching.dispose(); window.removeEventListener('resize', refit); clearTimeout(timer); refitRef.current = null; };
  }, [view]);
  // The text size is the root font size, set by the shell's own effect after this one runs: the refit waits its timer.
  const textSize = settings.textSize ?? 100;
  useEffect(() => { refitRef.current?.(); }, [textSize, phone]);
  // The phone layout's width is the window's, so a turned phone is a new one.
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 320 : window.innerWidth));
  useEffect(() => {
    if (!phone) return undefined;
    const on = () => setWidth(window.innerWidth);
    on();
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [phone]);
  const barPx = phoneBarWidth(width);

  return (
    <div className={phone ? 'vtt-phone-dock h-full min-h-0' : 'h-full min-h-0'} data-testid={`dock-${view}`} data-open-bar={phone ? openBar ?? 'none' : undefined}
      style={phone ? { width: `calc(100% + ${barPx * 2}px)`, marginLeft: -barPx, ['--vtt-phone-bar' as string]: `${barPx}px` } : undefined}>
      <KeptBoard.Provider value={kept.host}><PanelMemory.Provider value={memory.current}>
        <DockviewReact className="h-full" theme={settings.theme === 'light' ? themeLight : themeDark} components={components} tabComponents={tabComponents}
          rightHeaderActionsComponent={BarActions} disableDnd disableFloatingGroups onReady={ready} />
      </PanelMemory.Provider></KeptBoard.Provider>
      {kept.portal}
    </div>
  );
}
